import { jwtVerify } from 'jose';

/**
 * JWT Verification Utility for GeoComply Responses
 * 
 * This utility verifies JWT tokens returned by the backend GeoComply endpoint.
 * The tokens contain signed compliance data that cannot be tampered with.
 * 
 * SECURITY:
 * - Verifies JWT signature using JWT_SECRET
 * - Checks token expiration
 * - Prevents tampering with compliance status
 */

/**
 * Interface for the JWT payload from GeoComply backend
 */
export interface GeoComplyJwtPayload {
  isCompliant: boolean;
  location?: {
    country?: string;
    region?: string;
    city?: string;
    latitude?: number;
    longitude?: number;
  };
  timestamp: string;
  success: boolean;
  error?: string;
  exp: number; // Expiration timestamp
  iat?: number; // Issued at timestamp
}

/**
 * Verifies a JWT token from the GeoComply backend
 * 
 * This function:
 * 1. Verifies the JWT signature using the secret key
 * 2. Checks token expiration
 * 3. Returns the decoded payload if valid
 * 
 * @param token - JWT token string from backend
 * @param secret - Secret key for verification (from environment variable)
 * @returns Decoded JWT payload if valid, throws error if invalid
 */
export async function verifyGeoComplyToken(
  token: string,
  secret: string
): Promise<GeoComplyJwtPayload> {
  if (!token) {
    throw new Error('JWT token is required');
  }

  if (!secret) {
    throw new Error('JWT secret is required for token verification');
  }

  try {
    // Convert secret string to Uint8Array for jose library
    const secretKey = new TextEncoder().encode(secret);

    // Verify and decode the JWT token
    // This will throw an error if:
    // - Signature is invalid
    // - Token is expired
    // - Token format is invalid
    const { payload } = await jwtVerify(token, secretKey, {
      algorithms: ['HS256'], // Use HS256 (HMAC-SHA256) algorithm
    });

    // Type assertion to our payload interface
    const geoComplyPayload = payload as GeoComplyJwtPayload;

    // Additional validation: Check if required fields are present
    if (typeof geoComplyPayload.isCompliant !== 'boolean') {
      throw new Error('Invalid token payload: isCompliant is missing or invalid');
    }

    if (!geoComplyPayload.timestamp) {
      throw new Error('Invalid token payload: timestamp is missing');
    }

    // Check expiration manually (jwtVerify already does this, but double-check)
    if (geoComplyPayload.exp && geoComplyPayload.exp < Date.now() / 1000) {
      throw new Error('Token has expired');
    }

    return geoComplyPayload;
  } catch (error) {
    if (error instanceof Error) {
      // Provide more specific error messages
      if (error.message.includes('expired')) {
        throw new Error('GeoComply token has expired. Please check your location again.');
      }
      if (error.message.includes('signature')) {
        throw new Error('GeoComply token signature verification failed. Response may have been tampered with.');
      }
      throw new Error(`GeoComply token verification failed: ${error.message}`);
    }
    throw new Error('Unknown error during GeoComply token verification');
  }
}

/**
 * Gets the JWT secret from environment variables
 * 
 * The secret should match the backend's JWT_SECRET.
 * This is safe to expose in the frontend because it's only used for verification,
 * not for signing tokens.
 * 
 * @returns JWT secret string or empty string if not configured
 */
export function getJwtSecret(): string {
  // Try to get from environment variable
  // Note: In production, this should be set via build-time environment variables
  const secret = import.meta.env.VITE_JWT_SECRET || '';

  if (!secret) {
    console.warn(
      'JWT secret not configured. GeoComply token verification will fail. ' +
      'Set VITE_JWT_SECRET environment variable.'
    );
  }

  return secret;
}

